import { currencyFractionDigits } from 'v5/core/money/formatMoney';
import type { CurrencyCode } from 'v5/core/money/model';

/** What a resource rate can hold, as checks the codec and the rate field share. */

/** The largest amount a rate can hold, in major units. */
export const MAX_RATE_AMOUNT = 21_474_836.47;

export type AmountCheck =
  | { type: 'valid' }
  | { type: 'missing' }
  /** Unreadable text: the field holds NaN until the text parses. */
  | { type: 'not-a-number' }
  | { type: 'negative' }
  | { type: 'too-large'; max: number }
  | { type: 'too-many-decimals'; maxFractionDigits: number };

export type FeeCheck =
  | { type: 'valid' }
  | { type: 'not-a-number' }
  | { type: 'not-a-whole-percent' };

/** Values within this distance of a whole number of the smallest unit count as whole. */
const EPSILON = 1e-6;

export const rateRules = {
  amount(amount: number | null, currency: CurrencyCode): AmountCheck {
    if (amount === null) return { type: 'missing' };
    if (!Number.isFinite(amount)) return { type: 'not-a-number' };
    if (amount < 0) return { type: 'negative' };
    if (amount > MAX_RATE_AMOUNT)
      return { type: 'too-large', max: MAX_RATE_AMOUNT };
    const maxFractionDigits = Math.min(2, currencyFractionDigits(currency));
    const scaled = amount * 10 ** maxFractionDigits;
    if (Math.abs(scaled - Math.round(scaled)) > EPSILON) {
      return { type: 'too-many-decimals', maxFractionDigits };
    }
    return { type: 'valid' };
  },

  /** `null` means no fee. */
  fee(fee: number | null): FeeCheck {
    if (fee === null) return { type: 'valid' };
    if (!Number.isFinite(fee)) return { type: 'not-a-number' };
    if (!Number.isInteger(fee) || fee < 0 || fee > 100) {
      return { type: 'not-a-whole-percent' };
    }
    return { type: 'valid' };
  }
} as const;
